/**
 * fonts.ts — feature-detect which of the four families actually landed, and downgrade the
 * **design** rather than merely the font. `design-direction.md` §4.4.
 *
 * The three attributes this module writes on `<html>` are consumed by the degradation
 * blocks at the bottom of `styles/fonts.css`:
 *
 *   `data-display="fallback"`  Fira Sans Condensed is missing. The condensed fallbacks are
 *                              wider, so the hero steps down one size (`--fs-34/44/64` are
 *                              redefined) and tracking tightens, keeping a Russian title
 *                              inside three lines at 360px. The misregistration ghost,
 *                              which depends on exact glyph overlap, is switched off.
 *   `data-scrawl="off"`        Caveat is missing. Handwriting is *meaning* here (a
 *                              chinagraph pencil on an acetate), so it is not faked with a
 *                              script fallback — the scrawl is re-set in mono caps, the way
 *                              a pressing plant would have typeset it, and un-rotated.
 *   `data-mono="fallback"`     JetBrains Mono is missing. System mono has a different caps
 *                              width, so tracking drops from .16em to .08em; numerals stay
 *                              tabular.
 *
 * There is deliberately **no** attribute for Fira Sans, the UI face. Its failure is
 * absorbed entirely by the metric-matched `FS-fallback` face, so no design change is owed.
 *
 * ── Why this is more than the four lines in §4.4 ─────────────────────────────────────
 *
 * §4.4 sketches `document.fonts.ready.then(() => document.fonts.check(...))`. Three things
 * that sketch does not survive contact with:
 *
 *  1. **`check()` cannot see a failed download.** Per the CSS Font Loading spec, `check()`
 *     reports `false` only for faces that are `unloaded` or `loading`. A face whose woff2
 *     404s ends up `error` — and therefore reads as *available*. So this module also scans
 *     the `FontFaceSet` for an errored face in the family, which is exactly the state an
 *     unfilled `public/fonts/` cache produces. Without that scan the whole degradation
 *     path is dead code in the one scenario it exists for.
 *  2. **`unicode-range` makes loading lazy.** The Cyrillic subset is not even requested
 *     until a Cyrillic glyph is painted, which can be long after `fonts.ready` resolves.
 *     `observeFonts()` therefore re-probes on `loadingdone`/`loadingerror`, and
 *     `applyFontStatus` *removes* an attribute as readily as it sets one, so a late arrival
 *     un-degrades and a late failure degrades.
 *  3. **The API can be absent.** No `document.fonts` means no answer, and the honest
 *     default is the degraded design: every downgrade above is still legible under a real
 *     webfont, whereas assuming success overflows a Russian hero at 360px. Absence is
 *     reported as `supported: false` so a caller can log it.
 *
 * Nothing here blocks first paint. `font-display: swap` has a zero block period, the
 * fallback faces are metric-matched, and this module only ever toggles attributes — which
 * is what makes §4.4's absolute rule hold: *first paint must be readable with zero
 * webfonts.*
 */

/** The three families whose absence changes the design. */
export type FontKey = 'display' | 'scrawl' | 'mono';

export interface FontProbe {
  /** Key in {@link FontStatus} and the `data-*` suffix. */
  readonly key: FontKey;
  /** The family name as declared in `fonts.css` and named by `--ff-*` in `tokens.css`. */
  readonly family: string;
  /** Attribute written on `<html>`. Spelled out in full so it greps against the CSS. */
  readonly attribute: 'data-display' | 'data-scrawl' | 'data-mono';
  /** Attribute value used when the family did not survive. */
  readonly degraded: 'fallback' | 'off';
}

/**
 * `design-direction.md` §4.4, in order. Fira Sans (`--ff-ui`) is absent on purpose — see
 * the module header.
 */
export const FONT_PROBES: readonly FontProbe[] = Object.freeze([
  Object.freeze({
    key: 'display',
    family: 'Fira Sans Condensed',
    attribute: 'data-display',
    degraded: 'fallback',
  } as const),
  Object.freeze({
    key: 'scrawl',
    family: 'Caveat',
    attribute: 'data-scrawl',
    degraded: 'off',
  } as const),
  Object.freeze({
    key: 'mono',
    family: 'JetBrains Mono',
    attribute: 'data-mono',
    degraded: 'fallback',
  } as const),
]);

export interface FontStatus {
  /** `false` when the Font Loading API is unavailable, i.e. the three flags are guesses. */
  readonly supported: boolean;
  /** Fira Sans Condensed 800 is available. */
  readonly display: boolean;
  /** Caveat 600 is available. */
  readonly scrawl: boolean;
  /** JetBrains Mono is available. */
  readonly mono: boolean;
}

export interface FontsOptions {
  /** Document to probe. Defaults to the ambient `document`. */
  readonly document?: Document | undefined;
  /** Element carrying the `data-*` attributes. Defaults to that document's `<html>`. */
  readonly root?: HTMLElement | undefined;
}

/** Every family reported missing — the answer when we cannot ask. */
const ALL_DEGRADED: FontStatus = Object.freeze({
  supported: false,
  display: false,
  scrawl: false,
  mono: false,
});

/**
 * The `FontFaceSet`, or `null` where the API is missing or stubbed short. jsdom omits
 * `document.fonts` entirely, older WebViews omit `check`, and a Telegram WebView in a
 * hardened profile can expose the object with the method removed — all three land here.
 */
function fontFaceSet(doc: Document): FontFaceSet | null {
  const fonts: FontFaceSet | undefined = doc.fonts;
  if (fonts === null || fonts === undefined) return null;
  if (typeof fonts.check !== 'function') return null;
  return fonts;
}

/** `"Fira Sans"`, `'Fira Sans'` and `Fira Sans` all have to compare equal. */
function normaliseFamily(family: string): string {
  return family
    .trim()
    .replace(/^["']|["']$/g, '')
    .toLowerCase();
}

/**
 * `true` when the family is declared but at least one of its faces failed to load.
 *
 * This is the state an unfilled `public/fonts/` cache produces, and the state `check()`
 * cannot see. One errored subset is enough to degrade the whole family: a face set where
 * the Cyrillic file is missing falls back **mid-title** for a Russian string, which §4.1
 * calls out as destroying the look — a uniformly typeset fallback is strictly better than
 * a half-webfont title.
 */
function hasErroredFace(set: FontFaceSet, family: string): boolean {
  if (typeof set.forEach !== 'function') return false;
  const wanted = normaliseFamily(family);
  let declared = false;
  let errored = false;
  try {
    set.forEach((face: FontFace) => {
      if (normaliseFamily(face.family) !== wanted) return;
      declared = true;
      if (face.status === 'error') errored = true;
    });
  } catch {
    // A stubbed or hostile `forEach` must not take the whole boot sequence with it.
    return false;
  }
  return declared && errored;
}

/** Is this family usable right now? */
function familyAvailable(set: FontFaceSet, family: string): boolean {
  if (hasErroredFace(set, family)) return false;
  try {
    // §4.4's probe verbatim. The default probe text is a space, which lives in the `latin`
    // unicode-range, so this asks about the subset that always loads first.
    return set.check(`1rem "${family}"`);
  } catch {
    // `check()` throws on a font shorthand it cannot parse. Treat an unanswerable
    // question the same way as a missing API: assume the degraded design.
    return false;
  }
}

/**
 * Read the current state of the three families. Synchronous and side-effect free — call
 * {@link detectFonts} to also wait for loading to settle and write the attributes.
 */
export function probeFonts(doc: Document = document): FontStatus {
  const set = fontFaceSet(doc);
  if (set === null) return ALL_DEGRADED;

  const flags: Record<FontKey, boolean> = { display: false, scrawl: false, mono: false };
  for (const probe of FONT_PROBES) {
    flags[probe.key] = familyAvailable(set, probe.family);
  }
  return { supported: true, ...flags };
}

/**
 * Write (or clear) the three `data-*` attributes.
 *
 * Bidirectional on purpose: a family that is present has its attribute **removed**, so a
 * subset that arrives late — `unicode-range` defers the Cyrillic files until a Cyrillic
 * glyph is painted — un-degrades the design instead of leaving it stepped down forever.
 * Also makes the function idempotent, which is what lets {@link observeFonts} re-apply on
 * every font event.
 */
export function applyFontStatus(status: FontStatus, root: HTMLElement): void {
  for (const probe of FONT_PROBES) {
    if (status[probe.key]) root.removeAttribute(probe.attribute);
    else root.setAttribute(probe.attribute, probe.degraded);
  }
}

function resolve(options: FontsOptions): { doc: Document; root: HTMLElement } {
  const doc = options.document ?? document;
  return { doc, root: options.root ?? doc.documentElement };
}

/** `document.fonts.ready`, degraded to "already settled" wherever it is not a promise. */
async function whenSettled(set: FontFaceSet): Promise<void> {
  try {
    const ready: unknown = set.ready;
    if (typeof (ready as { then?: unknown } | null | undefined)?.then === 'function') {
      await (ready as Promise<unknown>);
    }
  } catch {
    // A rejected or absent `ready` is not a reason to skip the probe.
  }
}

/**
 * Await `document.fonts.ready`, probe, and write the attributes. This is §4.4's snippet,
 * hardened. Resolves with what was found so a caller can log it.
 */
export async function detectFonts(options: FontsOptions = {}): Promise<FontStatus> {
  const { doc, root } = resolve(options);
  const set = fontFaceSet(doc);
  if (set !== null) await whenSettled(set);
  const status = probeFonts(doc);
  applyFontStatus(status, root);
  return status;
}

/**
 * Keep the attributes honest after the first probe. `loadingdone` and `loadingerror` fire
 * for every lazily-triggered subset, which for us means every time a Cyrillic string is
 * painted for the first time in one of the four families.
 *
 * Returns a disposer. A no-op disposer is returned where there is no API to listen to.
 */
export function observeFonts(options: FontsOptions = {}): () => void {
  const { doc, root } = resolve(options);
  const set = fontFaceSet(doc);
  if (set === null || typeof set.addEventListener !== 'function') return () => {};

  const reapply = (): void => {
    applyFontStatus(probeFonts(doc), root);
  };
  set.addEventListener('loadingdone', reapply);
  set.addEventListener('loadingerror', reapply);

  return () => {
    if (typeof set.removeEventListener !== 'function') return;
    set.removeEventListener('loadingdone', reapply);
    set.removeEventListener('loadingerror', reapply);
  };
}

/**
 * The single call the app entry point makes: probe now, keep watching, hand back a
 * disposer. Fire-and-forget — nothing waits on the font probe, because nothing may.
 */
export function initFonts(options: FontsOptions = {}): () => void {
  void detectFonts(options);
  return observeFonts(options);
}
